import { useState, useEffect } from 'react';
import { Search, X, SlidersHorizontal } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface DirectorySearchBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  stateFilter: string;
  onStateFilterChange: (value: string) => void;
  licenseTypeFilter: string;
  onLicenseTypeFilterChange: (value: string) => void;
  specialtyFilter: string[];
  onSpecialtyFilterChange: (value: string[]) => void;
  countyFilter: string[];
  onCountyFilterChange: (value: string[]) => void;
  experienceFilter: string;
  onExperienceFilterChange: (value: string) => void;
  availableStates: string[];
  availableSpecialties: string[];
  availableCounties: string[];
  resultCount: number;
  totalCount: number;
}

const LICENSE_TYPES = [
  'Certified General',
  'Certified Residential',
  'Licensed',
  'Trainee',
];

const EXPERIENCE_RANGES = [
  { value: '1-5', label: '1–5 years' },
  { value: '5-10', label: '5–10 years' },
  { value: '10-20', label: '10–20 years' },
  { value: '20+', label: '20+ years' },
];

export function DirectorySearchBar({
  search,
  onSearchChange,
  stateFilter,
  onStateFilterChange,
  licenseTypeFilter,
  onLicenseTypeFilterChange,
  specialtyFilter,
  onSpecialtyFilterChange,
  countyFilter,
  onCountyFilterChange,
  experienceFilter,
  onExperienceFilterChange,
  availableStates,
  availableSpecialties,
  availableCounties,
  resultCount,
  totalCount,
}: DirectorySearchBarProps) {
  const [localSearch, setLocalSearch] = useState(search);
  const hasUrlFilters = (stateFilter !== '' && stateFilter !== 'all') ||
    (licenseTypeFilter !== '' && licenseTypeFilter !== 'all') ||
    specialtyFilter.length > 0 || countyFilter.length > 0 ||
    (experienceFilter !== '' && experienceFilter !== 'all');
  const [showFilters, setShowFilters] = useState(hasUrlFilters);

  // Sync localSearch when the external search prop changes (e.g., browser back/forward)
  useEffect(() => {
    setLocalSearch(search);
  }, [search]);

  // 400ms debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearch !== search) {
        onSearchChange(localSearch);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [localSearch]);

  const hasActiveFilters =
    search !== '' ||
    (stateFilter !== '' && stateFilter !== 'all') ||
    (licenseTypeFilter !== '' && licenseTypeFilter !== 'all') ||
    specialtyFilter.length > 0 ||
    countyFilter.length > 0 ||
    (experienceFilter !== '' && experienceFilter !== 'all');

  const clearAll = () => {
    setLocalSearch('');
    onSearchChange('');
    onStateFilterChange('all');
    onLicenseTypeFilterChange('all');
    onSpecialtyFilterChange([]);
    onCountyFilterChange([]);
    onExperienceFilterChange('all');
  };

  const toggleSpecialty = (specialty: string) => {
    if (specialtyFilter.includes(specialty)) {
      onSpecialtyFilterChange(specialtyFilter.filter(s => s !== specialty));
    } else {
      onSpecialtyFilterChange([...specialtyFilter, specialty]);
    }
  };

  const toggleCounty = (county: string) => {
    if (countyFilter.includes(county)) {
      onCountyFilterChange(countyFilter.filter(c => c !== county));
    } else {
      onCountyFilterChange([...countyFilter, county]);
    }
  };

  return (
    <div className="space-y-4">
      {/* Search row */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, company, or location..."
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            className="pl-10"
          />
          {localSearch && (
            <button
              onClick={() => { setLocalSearch(''); onSearchChange(''); }}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
            </button>
          )}
        </div>
        <Button
          variant={showFilters ? 'secondary' : 'outline'}
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          className="gap-2"
        >
          <SlidersHorizontal className="h-4 w-4" />
          <span className="hidden sm:inline">Filters</span>
          {hasActiveFilters && (
            <span className="h-2 w-2 rounded-full bg-primary" />
          )}
        </Button>
      </div>

      {/* Filter row */}
      {showFilters && (
        <div className="flex flex-wrap gap-3 items-end p-4 bg-muted/30 rounded-lg border">
          <div className="space-y-1.5">
            <label className="text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">State</label>
            <Select value={stateFilter || 'all'} onValueChange={onStateFilterChange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All States" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                {availableStates.map(state => (
                  <SelectItem key={state} value={state}>{state}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">License Type</label>
            <Select value={licenseTypeFilter || 'all'} onValueChange={onLicenseTypeFilterChange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {LICENSE_TYPES.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Experience</label>
            <Select value={experienceFilter || 'all'} onValueChange={onExperienceFilterChange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Any" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any</SelectItem>
                {EXPERIENCE_RANGES.map(r => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearAll} className="text-muted-foreground">
              <X className="h-3.5 w-3.5 mr-1" />
              Clear All
            </Button>
          )}
        </div>
      )}

      {/* County pills */}
      {showFilters && availableCounties.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <span className="text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground self-center mr-1">Counties:</span>
          {availableCounties.map(county => (
            <Badge
              key={county}
              variant={countyFilter.includes(county) ? 'default' : 'outline'}
              className="cursor-pointer text-xs"
              onClick={() => toggleCounty(county)}
            >
              {county}
            </Badge>
          ))}
        </div>
      )}

      {/* Specialty pills */}
      {showFilters && availableSpecialties.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <span className="text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground self-center mr-1">Specialties:</span>
          {availableSpecialties.slice(0, 12).map(specialty => (
            <Badge
              key={specialty}
              variant={specialtyFilter.includes(specialty) ? 'default' : 'outline'}
              className="cursor-pointer text-xs"
              onClick={() => toggleSpecialty(specialty)}
            >
              {specialty}
            </Badge>
          ))}
        </div>
      )}

      {/* Result count */}
      <p className="uppercase" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace", fontSize: '0.66rem', letterSpacing: '0.14em', color: 'var(--g5-sub)' }}>
        <span style={{ color: 'var(--g5-ink)', fontWeight: 600 }}>{resultCount}</span> of {totalCount} appraiser{totalCount !== 1 ? 's' : ''}
      </p>
    </div>
  );
}
